import Dexie from 'dexie';

export const dashboardDb = new Dexie('ASPIREdeckDashboardDB');

dashboardDb.version(16).stores({
  rawAnswers: 'key, userId, type, timestamp',
  subjectReasons: 'userId',
  jobposts: 'key, userId, subject, timestamp',
  superActivities: 'userId, timestamp',
  chosenTopSubject: 'userId',              // { userId, subject, chosenAt }
  visualizationsCache: 'key, userId, kind', 

  knowledgeResources: 'userId, timestamp',
  psGuidanceSets: 'userId, timestamp',
  challengeQuestions: 'subjectKey, timestamp',
  pastChallenges: 'key, userId, dateKey, timestamp',  
  challengeReflections: 'userId',
  planNotes: 'userId',                          // SuperCurricular "My Plan" journal
  resourceNotes: 'key, userId, subject',         // key = `${userId}_${subject}`, Knowledge page notes
  personalStatementAnswers: 'userId',            // UniStatement essay drafts
  userResources: 'key, userId, subject',        // key = `${userId}_${subject}`, user-added Knowledge resources
  optionCompletions: 'userId',                   // SuperCurricular suggested-option checkboxes
  activityStatuses: 'userId',                    // SuperCurricular activity status (not_started/in_progress/completed)
  knowledgeCheckboxes: 'key, userId, subject',   // key = `${userId}_${subject}`, "Completed" checkboxes on Knowledge page
  psStatusMap: 'userId',                         // UniStatement status per subject

  allJobpostsCache: 'userId, timestamp',
  starTemplatesGlobal: 'id, timestamp',
  interviewQuestionsGlobal: 'id, timestamp',
  skillProgress: 'key, userId, jobpostId, skillName',           // key = `${userId}_${jobpostId}_${skillName}`
  starNotes: 'key, userId, jobpostId, skillName, updatedAt',    // key = `${userId}_${jobpostId}_${skillName}`
  starIndex: 'userId',                                           // one row per user, data = [{jobpostId, skillName}]
  portfolioJobs: 'userId',        // shared shape used by both CareerDashboard.js and Dashboard.js
  extraCareerJobs: 'userId',
  interviewJobs: 'userId',
  interviewAnswers: 'userId',
  interviewStatuses: 'userId',
  portfolioStatus: 'userId',
  addonSections: 'userId',
  higherEducation: 'userId',
  starExperiences: 'userId',      // the user_star_answers_{userId} list

  assessmentProgress: 'key, userId, type',   // key = `${userId}_${type}`, mirrors progress_{userId}_{type}
  userProfile: 'userId',                      // username cache

  insightsCache: 'key, userId, kind',        // key = `${userId}_${kind}` — kind: 'career' | 'child_${childId}' etc.
  strategyCache: 'key, userId, kind',   // key = `${userId}_${kind}`, kind: 'self' | 'parent_view'

  calendarEvents: 'userId',              // one row per user, data = [ {id, title, category, startDate, endDate, ...} ]
  pathwayProgress: 'userId', 
  
  roadmaps: 'userId, type, generatedAt',           // Main roadmap
  roadmapReflections: 'userId, roadmapType',       // User feedback / profile intelligence
  userSettings: 'userId',       // { userId, grade, country, notes, updatedAt }

  aiSkillProgress: 'key, userId, facet, subSkillName',      // key = `${userId}_${facet}_${subSkillName}`
  aiSubskillJournal: 'key, userId, facet, subSkillName',    // key = same, data = [{text, progress, ts}]
  aiCourseNotes: 'userId',                                   // one row per user, data = { [rowId]: {...} }
  launchpadProjectsIndex: 'userId',                          // one row per user, data = [{id, name, ...}]
  launchpadProjectData: 'key, userId, projectId',            // key = `${userId}_${projectId}`
  launchpadProjectNotes: 'key, userId, projectId',
  launchpadProjectVcs: 'key, userId, projectId',
  launchpadProjectAdvisorNotes: 'key, userId, projectId',    // AI Venture Advisor: { nextSteps, marketingPlan, riskCompliance }
  seedFundLocation: 'userId',
  aiSkillsCache: 'userId, timestamp',              // LeaderDashboard AIXcel skills catalogue
  freeCoursesCache: 'userId, timestamp',           // LeaderDashboard AIXcel free courses
  investorDeckSectionsCache: 'id, timestamp',      // global, not user-scoped
  vcDatabaseCache: 'id, timestamp',                // global, not user-scoped

  xperienceProjectsIndex: 'userId',
  xperienceProjectData: 'key, userId, projectId',
  xperienceJournalResources: 'key, userId, projectId',
  xperienceConnections: 'key, userId, projectId',
  xperienceAdviceHistory: 'key, userId, projectId',
  xperienceTractionMetrics: 'key, userId, projectId',
  xperienceExtraTaskTs: 'key, userId, projectId',
  xperienceUsedTasks: 'key, userId, projectId, stage',
  xperienceSeenStages: 'key, userId, projectId, stage',
  xperienceWatchedResources: 'userId',
  xperienceProjectQuota: 'userId',
  xperienceStageInfoCache: 'key, userId, stage',

  coachCompletedResources: 'key, userId, skillIdentifier',   // key = `${userId}_${skillIdentifier}`
  coachXcelNotes: 'userId',                                   // one row per user, data = { general_*, resources_* }
  coachFacetDescriptionsCache: 'id, timestamp',                // global, not user-scoped
  coachSkillsCache: 'id, timestamp',                           // global, not user-scoped
  coachLearningResourcesCache: 'id, timestamp',                // global, not user-scoped
  coachFeedCategoriesCache: 'id, timestamp',                   // global, not user-scoped
  coachAssessmentCache: 'userId, timestamp',   
  
  coachDailyContentCache: 'key, userId, dateKey, difficulty, timestamp', // key = `${userId}_${dateKey}_${difficulty}`
  coachDailyProgress: 'userId',
  coachDailySeenCompetencies: 'userId',
  coachDailySelectedDifficulty: 'userId',
  coachDailyRefreshCooldown: 'userId',

  leaderCompletedResources: 'key, userId, skillIdentifier',   // key = `${userId}_${skillIdentifier}`
  leaderXcelNotes: 'userId',                                    // one row per user, data = { general_*, resources_* }
  leaderFacetDescriptionsCache: 'id, timestamp',                // global, not user-scoped
  leaderSkillsCache: 'id, timestamp',                           // global, not user-scoped
  leaderLearningResourcesCache: 'id, timestamp',                // global, not user-scoped
  leaderFeedCategoriesCache: 'id, timestamp',                   // global, not user-scoped
  leaderAssessmentCache: 'userId, timestamp',  
  
  starNotesBySkill: 'key, userId, skillName, updatedAt', // key = `${userId}_${normalizedSkillName}` — shared across all jobs
  skillProgressBySkill: 'key, userId, skillName, updatedAt', // key = `${userId}_${normalizedSkillName}` — shared across all jobs
  jobTrackerEntries: 'userId',
  learnerIntelligence: 'userId',
  studyLearnerIntelligence: 'userId',
  interviewCustomQuestions: 'key, userId, jobpostId', // key = `${userId}_${jobpostId}`, data = [{ id, question, answer, planOfAttack, recruiterPerspective, createdAt }]

  leaderDailyContentCache: 'key, userId, dateKey, difficulty, timestamp', // key = `${userId}_${dateKey}_${difficulty}`
  leaderDailyProgress: 'userId',
  leaderDailySeenCompetencies: 'userId',
  leaderDailySelectedDifficulty: 'userId',
  leaderDailyRefreshCooldown: 'userId',

  profileGroupsCache: 'groupName, timestamp',
  profileMatchesCache: 'key, userId, kind',

  subjectFacetsCache: 'id, timestamp',
});

// ---------- Profile Matches cache (career / study) ----------
export async function getProfileMatchesCache(userId, kind) {
  const key = `${userId}_${kind}`;
  return (await dashboardDb.profileMatchesCache.get(key)) || null; // { key, userId, kind, matches, timestamp }
}

export async function saveProfileMatchesCache(userId, kind, matches) {
  const key = `${userId}_${kind}`;
  await dashboardDb.profileMatchesCache.put({
    key,
    userId,
    kind,
    matches,
    timestamp: Date.now(),
  });
}

export async function bustProfileMatchesCache(userId) {
  if (!userId) {
    await dashboardDb.profileMatchesCache.clear();
    return;
  }
  await dashboardDb.profileMatchesCache.where('userId').equals(userId).delete();
  await dashboardDb.profileMatchesCache.where('userId').equals(String(userId)).delete();
}

// ---------- Raw Answers (Subject / SubjectPart3 / Career / Learning) ----------
export async function getRawAnswers(userId, type) {
  const key = `${userId}_${type}`;
  return (await dashboardDb.rawAnswers.get(key)) || null; // { key, userId, type, data, sessionId, timestamp }
}

export async function saveRawAnswers(userId, type, data, sessionId = null) {
  const key = `${userId}_${type}`;
  await dashboardDb.rawAnswers.put({ key, userId, type, data, sessionId, timestamp: Date.now() });
}

export async function clearRawAnswers(userId, type) {
  await dashboardDb.rawAnswers.delete(`${userId}_${type}`);
}

// ---------- Subject Reasons ----------
export async function getSubjectReasons(userId) {
  const row = await dashboardDb.subjectReasons.get(userId);
  return row?.data || {};
}

export async function saveSubjectReasons(userId, data) {
  await dashboardDb.subjectReasons.put({ userId, data, updatedAt: Date.now() });
}

// ---------- Job posts (per subject) ----------
export async function getJobposts(userId, subject) {
  const key = `${userId}_${subject}`;
  return (await dashboardDb.jobposts.get(key)) || null; // { key, userId, subject, data, timestamp }
}

export async function saveJobposts(userId, subject, data) {
  const key = `${userId}_${subject}`;
  await dashboardDb.jobposts.put({ key, userId, subject, data, timestamp: Date.now() });
}

export async function bustJobpostsCache() {
  await dashboardDb.jobposts.clear();
}


// ---------- Super-curricular activities (grouped by subject, one row per user) ----------
export async function getSuperActivities(userId) {
  const row = await dashboardDb.superActivities.get(userId);
  return row || { userId, data: {}, timestamp: 0 };
}

export async function saveSuperActivities(userId, data) {
  await dashboardDb.superActivities.put({ userId, data, timestamp: Date.now() });
}

export async function bustSuperActivitiesCache() {
  await dashboardDb.superActivities.clear();
}


// ---------- Knowledge resources (grouped by subject, one row per user) ----------
export async function getKnowledgeResources(userId) {
  const row = await dashboardDb.knowledgeResources.get(userId);
  return row || { userId, data: {}, timestamp: 0 };
}

export async function saveKnowledgeResources(userId, data) {
  await dashboardDb.knowledgeResources.put({ userId, data, timestamp: Date.now() });
}

export async function bustKnowledgeResourcesCache() {
  await dashboardDb.knowledgeResources.clear();
}

// ---------- Personal Statement guidance sets (grouped by subject, one row per user) ----------
export async function getPsGuidanceSets(userId) {
  const row = await dashboardDb.psGuidanceSets.get(userId);
  return row || { userId, data: {}, timestamp: 0 };
}

export async function savePsGuidanceSets(userId, data) {
  await dashboardDb.psGuidanceSets.put({ userId, data, timestamp: Date.now() });
}

export async function bustPsGuidanceCache() {
  await dashboardDb.psGuidanceSets.clear();
}

// ---------- Team challenge questions (per subject, not user-scoped) ----------
export async function getChallengeQuestions(subjectName) {
  const key = subjectName.toLowerCase().trim();
  const row = await dashboardDb.challengeQuestions.get(key);
  return row || null; // { subjectKey, data, timestamp }
}

export async function saveChallengeQuestions(subjectName, data) {
  const key = subjectName.toLowerCase().trim();
  await dashboardDb.challengeQuestions.put({ subjectKey: key, data, timestamp: Date.now() });
}

export async function bustChallengeQuestionsCache() {
  await dashboardDb.challengeQuestions.clear();
}

// ---------- Past team challenges (daily-cached list) ----------
export async function getPastChallengesCache(userId, dateKey) {
  const key = `${userId}_${dateKey}`;
  return (await dashboardDb.pastChallenges.get(key)) || null; // { key, userId, dateKey, data, timestamp }
}

export async function savePastChallengesCache(userId, dateKey, data) {
  const key = `${userId}_${dateKey}`;
  await dashboardDb.pastChallenges.put({ key, userId, dateKey, data, timestamp: Date.now() });
}

// ---------- Structured challenge reflections ----------
export async function getChallengeReflections(userId) {
  const row = await dashboardDb.challengeReflections.get(userId);
  return row?.data || {};
}

export async function saveChallengeReflections(userId, data) {
  await dashboardDb.challengeReflections.put({ userId, data, updatedAt: Date.now() });
}

// ---------- Past team challenges: append helper (used right after submission) ----------
export async function appendPastChallenge(userId, dateKey, challenge) {
  const existing = await getPastChallengesCache(userId, dateKey);
  const list = existing?.data ? [...existing.data] : [];
  // De-dupe by taskId, keep newest 50
  const taskId = challenge?.task?.task_id || challenge?.task?.taskId;
  const filtered = list.filter((c) => (c?.task?.task_id || c?.task?.taskId) !== taskId);
  const updated = [challenge, ...filtered].slice(0, 50);
  await savePastChallengesCache(userId, dateKey, updated);
  return updated;
}

// ---------- All jobposts (full catalogue, per user) ----------
export async function getAllJobpostsCache(userId) {
  return (await dashboardDb.allJobpostsCache.get(userId)) || null; // { userId, data, timestamp }
}

export async function saveAllJobpostsCache(userId, data) {
  await dashboardDb.allJobpostsCache.put({ userId, data, timestamp: Date.now() });
}

export async function bustAllJobpostsCache() {
  await dashboardDb.allJobpostsCache.clear();
}

// ---------- STAR templates (global, not user-scoped) ----------
export async function getStarTemplatesGlobal() {
  return (await dashboardDb.starTemplatesGlobal.get('global')) || null;
}

export async function saveStarTemplatesGlobal(data) {
  await dashboardDb.starTemplatesGlobal.put({ id: 'global', data, timestamp: Date.now() });
}

export async function bustStarTemplatesGlobal() {
  await dashboardDb.starTemplatesGlobal.clear();
}

// ---------- Top interview questions (global, not user-scoped) ----------
export async function getInterviewQuestionsGlobal() {
  return (await dashboardDb.interviewQuestionsGlobal.get('global')) || null;
}

export async function saveInterviewQuestionsGlobal(data) {
  await dashboardDb.interviewQuestionsGlobal.put({ id: 'global', data, timestamp: Date.now() });
}

export async function bustInterviewQuestionsGlobal() {
  await dashboardDb.interviewQuestionsGlobal.clear();
}

// ---------- Skill progress rings ----------
export async function getSkillProgress(userId, jobpostId, skillName) {
  const key = `${userId}_${jobpostId}_${skillName}`;
  const row = await dashboardDb.skillProgress.get(key);
  return row?.value ?? 0;
}

export async function saveSkillProgress(userId, jobpostId, skillName, value) {
  const key = `${userId}_${jobpostId}_${skillName}`;
  await dashboardDb.skillProgress.put({ key, userId, jobpostId, skillName, value });
}

// ---------- STAR notes (per skill, per job) ----------
export async function getStarNote(userId, jobpostId, skillName) {
  const key = `${userId}_${jobpostId}_${skillName}`;
  return (await dashboardDb.starNotes.get(key)) || null; // { key, userId, jobpostId, skillName, s, t, a, r, updatedAt }
}

export async function saveStarNote(userId, jobpostId, skillName, fields) {
  const key = `${userId}_${jobpostId}_${skillName}`;
  const existing = await dashboardDb.starNotes.get(key);
  const updated = {
    key, userId, jobpostId, skillName,
    s: existing?.s || '', t: existing?.t || '', a: existing?.a || '', r: existing?.r || '',
    ...fields,
    updatedAt: new Date().toISOString(),
  };
  await dashboardDb.starNotes.put(updated);

  // Maintain the index used by the STAR Bank digest
  const index = await getStarIndex(userId);
  const exists = index.some(e => String(e.jobpostId) === String(jobpostId) && e.skillName === skillName);
  if (!exists) {
    await saveStarIndex(userId, [...index, { jobpostId: String(jobpostId), skillName }]);
  }
  return updated;
}

export async function clearStarNote(userId, jobpostId, skillName) {
  const key = `${userId}_${jobpostId}_${skillName}`;
  await dashboardDb.starNotes.delete(key);
  const index = await getStarIndex(userId);
  const filtered = index.filter(e => !(String(e.jobpostId) === String(jobpostId) && e.skillName === skillName));
  await saveStarIndex(userId, filtered);
}

// ---------- STAR index (jobpostId+skillName pairs with any content) ----------
export async function getStarIndex(userId) {
  const row = await dashboardDb.starIndex.get(userId);
  return row?.data || [];
}

export async function saveStarIndex(userId, data) {
  await dashboardDb.starIndex.put({ userId, data });
}

// ---------- Fetch ALL star notes for a user at once (STAR Bank digest) ----------
export async function getAllStarNotesForUser(userId) {
  return await dashboardDb.starNotes.where('userId').equals(userId).toArray();
}

// ---------- Portfolio jobs (shared shape: also read by Dashboard.js) ----------
export async function getPortfolioJobs(userId) {
  const row = await dashboardDb.portfolioJobs.get(userId);
  return row?.data || [];
}

export async function savePortfolioJobs(userId, data) {
  await dashboardDb.portfolioJobs.put({ userId, data });
}

// ---------- Extra career jobs (Compass "Extra Career Options") ----------
export async function getExtraCareerJobs(userId) {
  const row = await dashboardDb.extraCareerJobs.get(userId);
  return row?.data || [];
}

export async function saveExtraCareerJobs(userId, data) {
  await dashboardDb.extraCareerJobs.put({ userId, data });
}

// ---------- Interview jobs / answers / statuses ----------
export async function getInterviewJobs(userId) {
  const row = await dashboardDb.interviewJobs.get(userId);
  return row?.data || [];
}
export async function saveInterviewJobs(userId, data) {
  await dashboardDb.interviewJobs.put({ userId, data });
}

export async function getInterviewAnswers(userId) {
  const row = await dashboardDb.interviewAnswers.get(userId);
  return row?.data || {};
}
export async function saveInterviewAnswers(userId, data) {
  await dashboardDb.interviewAnswers.put({ userId, data });
}

export async function getInterviewStatuses(userId) {
  const row = await dashboardDb.interviewStatuses.get(userId);
  return row?.data || {};
}
export async function saveInterviewStatuses(userId, data) {
  await dashboardDb.interviewStatuses.put({ userId, data });
}

export async function getPortfolioStatus(userId) {
  const row = await dashboardDb.portfolioStatus.get(userId);
  return row?.data || 'not_started';
}
export async function savePortfolioStatus(userId, status) {
  await dashboardDb.portfolioStatus.put({ userId, data: status });
}

export async function getAddonSections(userId) {
  const row = await dashboardDb.addonSections.get(userId);
  return row?.data || { professionaltechnicalSkills: [], personalSkills: [], higherEducation: [], qualificationsAwards: [] };
}
export async function saveAddonSections(userId, data) {
  await dashboardDb.addonSections.put({ userId, data });
}

export async function getHigherEducation(userId) {
  const row = await dashboardDb.higherEducation.get(userId);
  return row?.data || [];
}
export async function saveHigherEducation(userId, data) {
  await dashboardDb.higherEducation.put({ userId, data });
}

export async function getStarExperiences(userId) {
  const row = await dashboardDb.starExperiences.get(userId);
  return row?.data || [];
}
export async function saveStarExperiences(userId, data) {
  await dashboardDb.starExperiences.put({ userId, data });
}

// ---------- Assessment progress (session tracking per assessment type) ----------
export async function getAssessmentProgress(userId, type) {
  const key = `${userId}_${type}`;
  return (await dashboardDb.assessmentProgress.get(key)) || null; // { key, userId, type, ...progress, timestamp }
}
export async function saveAssessmentProgress(userId, type, progressData) {
  const key = `${userId}_${type}`;
  await dashboardDb.assessmentProgress.put({ key, userId, type, ...progressData, timestamp: Date.now() });
}

// ---------- Username cache ----------
export async function getUserProfile(userId) {
  return (await dashboardDb.userProfile.get(userId)) || null; // { userId, username, timestamp }
}
export async function saveUserProfile(userId, username) {
  await dashboardDb.userProfile.put({ userId, username, timestamp: Date.now() });
}

// ---------- Insights cache (career insights, child insights, etc.) ----------
export async function getInsightsCache(userId, kind = 'career') {
  const key = `${userId}_${kind}`;
  return (await dashboardDb.insightsCache.get(key)) || null; // { key, userId, kind, insights, generationDate, timestamp }
}
export async function saveInsightsCache(userId, kind, insights, generationDate) {
  const key = `${userId}_${kind}`;
  await dashboardDb.insightsCache.put({ key, userId, kind, insights, generationDate, timestamp: Date.now() });
}

// ---------- Strategy cache ----------
export async function getStrategyCache(userId, kind = 'self') {
  const key = `${userId}_${kind}`;
  return (await dashboardDb.strategyCache.get(key)) || null; // { key, userId, kind, strategy, generationDate, timestamp }
}

export async function saveStrategyCache(userId, kind, strategy, generationDate) {
  const key = `${userId}_${kind}`;
  await dashboardDb.strategyCache.put({ key, userId, kind, strategy, generationDate, timestamp: Date.now() });
}

// ---------- Calendar events ----------
export async function getCalendarEvents(userId) {
  const row = await dashboardDb.calendarEvents.get(userId);
  return row?.data || [];
}

export async function saveCalendarEvents(userId, data) {
  await dashboardDb.calendarEvents.put({ userId, data });
}

// ---------- Personalised Roadmaps ----------
export async function getRoadmap(userId, type = 'career_3_year_plan') {
  return (await dashboardDb.roadmaps.get({ userId, type })) || null;
}

export async function saveRoadmap(userId, type, content, basedOn) {
  await dashboardDb.roadmaps.put({
    userId,
    type,
    roadmapContent: content,
    generatedAt: new Date().toISOString(),
    basedOn,
    updatedAt: Date.now()
  });
}

// ---------- Roadmap Reflections (rich learner profile data) ----------
export async function getRoadmapReflections(userId, roadmapType = 'career_3_year_plan') {
  const row = await dashboardDb.roadmapReflections.get({ userId, roadmapType });
  return row?.data || {};
}

export async function saveRoadmapReflections(userId, roadmapType, reflections) {
  await dashboardDb.roadmapReflections.put({
    userId,
    roadmapType,
    data: reflections,
    updatedAt: Date.now()
  });
}

// ---------- SuperCurricular Plan Notes (goal, log entries, reflection) ----------
export async function getPlanNotes(userId) {
  const row = await dashboardDb.planNotes.get(userId);
  return row?.data || {};
}

export async function savePlanNotes(userId, data) {
  await dashboardDb.planNotes.put({ userId, data, updatedAt: Date.now() });
}

// ---------- Knowledge resource notes (per subject) ----------
export async function getResourceNotes(userId, subject) {
  const key = `${userId}_${subject.toLowerCase().trim()}`;
  const row = await dashboardDb.resourceNotes.get(key);
  return row?.data || {};
}

export async function saveResourceNotes(userId, subject, data) {
  const key = `${userId}_${subject.toLowerCase().trim()}`;
  await dashboardDb.resourceNotes.put({ key, userId, subject: subject.toLowerCase().trim(), data, updatedAt: Date.now() });
}

// ---------- Personal Statement (UniStatement) answers ----------
export async function getPersonalStatementAnswers(userId) {
  const row = await dashboardDb.personalStatementAnswers.get(userId);
  return row?.data || {};
}

export async function savePersonalStatementAnswers(userId, data) {
  await dashboardDb.personalStatementAnswers.put({ userId, data, updatedAt: Date.now() });
}

// ---------- User-added Knowledge resources (per subject) ----------
export async function getUserResources(userId, subject) {
  const key = `${userId}_${subject.toLowerCase().trim()}`;
  const row = await dashboardDb.userResources.get(key);
  return row?.data || { print: [], video: [], audio: [], 'free-course': [] };
}

export async function saveUserResources(userId, subject, data) {
  const key = `${userId}_${subject.toLowerCase().trim()}`;
  await dashboardDb.userResources.put({ key, userId, subject: subject.toLowerCase().trim(), data, updatedAt: Date.now() });
}

// ---------- SuperCurricular option completions ----------
export async function getOptionCompletions(userId) {
  const row = await dashboardDb.optionCompletions.get(userId);
  return row?.data || {};
}

export async function saveOptionCompletions(userId, data) {
  await dashboardDb.optionCompletions.put({ userId, data, updatedAt: Date.now() });
}

// ---------- SuperCurricular activity statuses ----------
export async function getActivityStatuses(userId) {
  const row = await dashboardDb.activityStatuses.get(userId);
  return row?.data || {};
}

export async function saveActivityStatuses(userId, data) {
  await dashboardDb.activityStatuses.put({ userId, data, updatedAt: Date.now() });
}

// ---------- Knowledge page "Completed" checkboxes (per subject) ----------
export async function getKnowledgeCheckboxes(userId, subject) {
  const key = `${userId}_${subject.toLowerCase().trim()}`;
  const row = await dashboardDb.knowledgeCheckboxes.get(key);
  return row?.data || {};
}

export async function saveKnowledgeCheckboxes(userId, subject, data) {
  const key = `${userId}_${subject.toLowerCase().trim()}`;
  await dashboardDb.knowledgeCheckboxes.put({ key, userId, subject: subject.toLowerCase().trim(), data, updatedAt: Date.now() });
}

// ---------- UniStatement status (per subject) ----------
export async function getPsStatus(userId, subject) {
  const row = await dashboardDb.psStatusMap.get(userId);
  const map = row?.data || {};
  return map[subject.toLowerCase().trim()] || 'not_started';
}

export async function savePsStatus(userId, subject, status) {
  const row = await dashboardDb.psStatusMap.get(userId);
  const map = row?.data || {};
  map[subject.toLowerCase().trim()] = status;
  await dashboardDb.psStatusMap.put({ userId, data: map, updatedAt: Date.now() });
}

// ---------- Chosen Top Subject ----------
export async function getChosenTopSubject(userId) {
  const row = await dashboardDb.chosenTopSubject.get(userId);
  return row?.subject || null;
}
 
export async function saveChosenTopSubject(userId, subject) {
  await dashboardDb.chosenTopSubject.put({ userId, subject, chosenAt: Date.now() });
}
 
// ---------- Visualizations cache (Career / Learning radar data) ----------
export async function getVisualizationsCache(userId, kind) {
  const key = `${userId}_${kind}`;
  return (await dashboardDb.visualizationsCache.get(key)) || null;
}
 
export async function saveVisualizationsCache(userId, kind, data) {
  const key = `${userId}_${kind}`;
  await dashboardDb.visualizationsCache.put({ key, userId, kind, data, timestamp: Date.now() });
}

// ==================== PATHWAY PROGRESS (NEW - DEXIE) ====================
export async function getPathwayProgress(userId) {
  if (!userId) return null;
  const row = await dashboardDb.pathwayProgress.get(userId);
  return row?.data || null;
}

export async function savePathwayProgress(userId, data) {
  if (!userId) return;
  await dashboardDb.pathwayProgress.put({
    userId,
    data,
    updatedAt: Date.now()
  });
}

// ---------- User Settings (grade, country) ----------
export async function getUserSettings(userId) {
  const row = await dashboardDb.userSettings.get(userId);
  return row || { grade: 'Year 7', country: '' };
}

export async function saveUserSettings(userId, settings) {
  // Only store the fields passed; 'notes' is intentionally omitted.
  await dashboardDb.userSettings.put({ userId, ...settings, updatedAt: Date.now() });
}

// ---------- AI Skill Progress (sub-skill mastery rings) ----------
const aiSkillProgressKey = (userId, facet, subSkillName) =>
  `${userId}_${facet.replace(/[^a-zA-Z0-9]/g, '_')}_${subSkillName.replace(/[^a-zA-Z0-9]/g, '_')}`;

// Bulk-load ALL progress rows for a user into a flat map: { `${facet}__${subSkillName}`: value }
export async function getAllAiSkillProgress(userId) {
  const rows = await dashboardDb.aiSkillProgress.where('userId').equals(userId).toArray();
  const map = {};
  rows.forEach(r => { map[`${r.facet}__${r.subSkillName}`] = r.value; });
  return map;
}

export async function saveAiSkillProgress(userId, facet, subSkillName, value) {
  const key = aiSkillProgressKey(userId, facet, subSkillName);
  await dashboardDb.aiSkillProgress.put({ key, userId, facet, subSkillName, value });
}

// ---------- Development Journal (per sub-skill) ----------
const journalKey = (userId, facet, subSkillName) =>
  `${userId}_${facet.replace(/[^a-zA-Z0-9]/g, '_')}_${subSkillName.replace(/[^a-zA-Z0-9]/g, '_')}`;

export async function getAiSubskillJournal(userId, facet, subSkillName) {
  const key = journalKey(userId, facet, subSkillName);
  const row = await dashboardDb.aiSubskillJournal.get(key);
  return row?.data || [];
}

export async function saveAiSubskillJournal(userId, facet, subSkillName, entries) {
  const key = journalKey(userId, facet, subSkillName);
  await dashboardDb.aiSubskillJournal.put({ key, userId, facet, subSkillName, data: entries, updatedAt: Date.now() });
}

// ---------- AI Course Notes (Free Courses tab, per user) ----------
export async function getAiCourseNotes(userId) {
  const row = await dashboardDb.aiCourseNotes.get(userId);
  return row?.data || {};
}

export async function saveAiCourseNotes(userId, data) {
  await dashboardDb.aiCourseNotes.put({ userId, data, updatedAt: Date.now() });
}

// ---------- LaunchPad Projects Index ----------
export async function getLpProjectsIndex(userId) {
  const row = await dashboardDb.launchpadProjectsIndex.get(userId);
  return row?.data || [];
}

export async function saveLpProjectsIndex(userId, projects) {
  await dashboardDb.launchpadProjectsIndex.put({ userId, data: projects, updatedAt: Date.now() });
}

// ---------- LaunchPad Project Data (deck section content) ----------
export async function getLpProjectData(userId, projectId) {
  const key = `${userId}_${projectId}`;
  const row = await dashboardDb.launchpadProjectData.get(key);
  return row?.data || {};
}

export async function saveLpProjectData(userId, projectId, data) {
  const key = `${userId}_${projectId}`;
  await dashboardDb.launchpadProjectData.put({ key, userId, projectId, data, updatedAt: Date.now() });
}

export async function deleteLpProjectData(userId, projectId) {
  await dashboardDb.launchpadProjectData.delete(`${userId}_${projectId}`);
}

// ---------- LaunchPad Project Notes (rich text) ----------
export async function getLpProjectNotes(userId, projectId) {
  const key = `${userId}_${projectId}`;
  const row = await dashboardDb.launchpadProjectNotes.get(key);
  return row?.data || "";
}

export async function saveLpProjectNotes(userId, projectId, notes) {
  const key = `${userId}_${projectId}`;
  await dashboardDb.launchpadProjectNotes.put({ key, userId, projectId, data: notes, updatedAt: Date.now() });
}

export async function deleteLpProjectNotes(userId, projectId) {
  await dashboardDb.launchpadProjectNotes.delete(`${userId}_${projectId}`);
}

// ---------- LaunchPad VC / Grant Shortlist ----------
export async function getLpProjectVcs(userId, projectId) {
  const key = `${userId}_${projectId}`;
  const row = await dashboardDb.launchpadProjectVcs.get(key);
  return row?.data || {};
}

export async function saveLpProjectVcs(userId, projectId, shortlist) {
  const key = `${userId}_${projectId}`;
  await dashboardDb.launchpadProjectVcs.put({ key, userId, projectId, data: shortlist, updatedAt: Date.now() });
}

export async function deleteLpProjectVcs(userId, projectId) {
  await dashboardDb.launchpadProjectVcs.delete(`${userId}_${projectId}`);
}

// ---------- LaunchPad AI Venture Advisor notes ----------
export async function getLpProjectAdvisorNotes(userId, projectId) {
  const key = `${userId}_${projectId}`;
  const row = await dashboardDb.launchpadProjectAdvisorNotes.get(key);
  return row?.data || {};
}

export async function saveLpProjectAdvisorNotes(userId, projectId, data) {
  const key = `${userId}_${projectId}`;
  await dashboardDb.launchpadProjectAdvisorNotes.put({ key, userId, projectId, data, updatedAt: Date.now() });
}

export async function deleteLpProjectAdvisorNotes(userId, projectId) {
  await dashboardDb.launchpadProjectAdvisorNotes.delete(`${userId}_${projectId}`);
}

// ---------- Seed Fund location ----------
export async function getSeedFundLocation(userId) {
  const row = await dashboardDb.seedFundLocation.get(userId);
  return row?.location || '';
}

export async function saveSeedFundLocation(userId, location) {
  await dashboardDb.seedFundLocation.put({ userId, location, updatedAt: Date.now() });
}

// ---------- AI Skills catalogue cache (LeaderDashboard/AIXcel, 60-day) ----------
export async function getAiSkillsCache(userId) {
  return (await dashboardDb.aiSkillsCache.get(userId)) || null; // { userId, data, timestamp }
}

export async function saveAiSkillsCache(userId, data) {
  await dashboardDb.aiSkillsCache.put({ userId, data, timestamp: Date.now() });
}

export async function bustAiSkillsCache() {
  await dashboardDb.aiSkillsCache.clear();
}

// ---------- Free courses cache (LeaderDashboard/AIXcel, 60-day) ----------
export async function getFreeCoursesCache(userId) {
  return (await dashboardDb.freeCoursesCache.get(userId)) || null;
}

export async function saveFreeCoursesCache(userId, data) {
  await dashboardDb.freeCoursesCache.put({ userId, data, timestamp: Date.now() });
}

export async function bustFreeCoursesCache() {
  await dashboardDb.freeCoursesCache.clear();
}

// ---------- Investor deck sections cache (global, not user-scoped, 60-day) ----------
export async function getInvestorDeckSectionsCache() {
  return (await dashboardDb.investorDeckSectionsCache.get('global')) || null;
}

export async function saveInvestorDeckSectionsCache(data) {
  await dashboardDb.investorDeckSectionsCache.put({ id: 'global', data, timestamp: Date.now() });
}

export async function bustInvestorDeckSectionsCache() {
  await dashboardDb.investorDeckSectionsCache.clear();
}

// ---------- VC database cache (global, not user-scoped, 60-day) ----------
export async function getVcDatabaseCache() {
  return (await dashboardDb.vcDatabaseCache.get('global')) || null;
}

export async function saveVcDatabaseCache(data) {
  await dashboardDb.vcDatabaseCache.put({ id: 'global', data, timestamp: Date.now() });
}

export async function bustVcDatabaseCache() {
  await dashboardDb.vcDatabaseCache.clear();
}

// ---------- Profile groups cache (FutureReady facet-group profile sets: facets
// + profiles + centroids, global, not user-scoped, TTL enforced by the caller
// — see profileMatching.js's default 24h) ----------
export async function getProfileGroupCache(groupName) {
  return (await dashboardDb.profileGroupsCache.get(groupName)) || null; // { groupName, data, timestamp }
}
 
export async function saveProfileGroupCache(groupName, data) {
  await dashboardDb.profileGroupsCache.put({ groupName, data, timestamp: Date.now() });
}
 
export async function bustProfileGroupsCache() {
  await dashboardDb.profileGroupsCache.clear();
}

// ================== STARTUP XPERIENCE ==================

// ---------- Projects Index ----------
export async function getXpProjectsIndex(userId) {
  const row = await dashboardDb.xperienceProjectsIndex.get(userId);
  return row?.data || [];
}
export async function saveXpProjectsIndex(userId, projects) {
  await dashboardDb.xperienceProjectsIndex.put({ userId, data: projects, updatedAt: Date.now() });
}

// ---------- Project Data (idea/state, history, current task) ----------
export async function getXpProjectData(userId, projectId) {
  const key = `${userId}_${projectId}`;
  return (await dashboardDb.xperienceProjectData.get(key)) || null; // { key, userId, projectId, user, history, currentTask, savedAt }
}
export async function saveXpProjectData(userId, projectId, user, history, currentTask) {
  const key = `${userId}_${projectId}`;
  await dashboardDb.xperienceProjectData.put({ key, userId, projectId, user, history, currentTask, savedAt: Date.now() });
}
export async function deleteXpProjectData(userId, projectId) {
  await dashboardDb.xperienceProjectData.delete(`${userId}_${projectId}`);
}

// ---------- Journal (Reference Library) Resources ----------
export async function getXpJournalResources(userId, projectId) {
  const row = await dashboardDb.xperienceJournalResources.get(`${userId}_${projectId}`);
  return row?.data || [];
}
export async function saveXpJournalResources(userId, projectId, data) {
  const key = `${userId}_${projectId}`;
  await dashboardDb.xperienceJournalResources.put({ key, userId, projectId, data, updatedAt: Date.now() });
}
export async function deleteXpJournalResources(userId, projectId) {
  await dashboardDb.xperienceJournalResources.delete(`${userId}_${projectId}`);
}

// ---------- Connections (Founder's Network) ----------
export async function getXpConnections(userId, projectId) {
  const row = await dashboardDb.xperienceConnections.get(`${userId}_${projectId}`);
  return row?.data || [];
}
export async function saveXpConnections(userId, projectId, data) {
  const key = `${userId}_${projectId}`;
  await dashboardDb.xperienceConnections.put({ key, userId, projectId, data, updatedAt: Date.now() });
}
export async function deleteXpConnections(userId, projectId) {
  await dashboardDb.xperienceConnections.delete(`${userId}_${projectId}`);
}

// ---------- AI Advice History ----------
export async function getXpAdviceHistory(userId, projectId) {
  const row = await dashboardDb.xperienceAdviceHistory.get(`${userId}_${projectId}`);
  return row?.data || [];
}
export async function saveXpAdviceHistory(userId, projectId, data) {
  const key = `${userId}_${projectId}`;
  await dashboardDb.xperienceAdviceHistory.put({ key, userId, projectId, data, updatedAt: Date.now() });
}
export async function deleteXpAdviceHistory(userId, projectId) {
  await dashboardDb.xperienceAdviceHistory.delete(`${userId}_${projectId}`);
}

// ---------- Traction Metrics ----------
export async function getXpTractionMetrics(userId, projectId) {
  const row = await dashboardDb.xperienceTractionMetrics.get(`${userId}_${projectId}`);
  return row?.data || [];
}
export async function saveXpTractionMetrics(userId, projectId, data) {
  const key = `${userId}_${projectId}`;
  await dashboardDb.xperienceTractionMetrics.put({ key, userId, projectId, data, updatedAt: Date.now() });
}
export async function deleteXpTractionMetrics(userId, projectId) {
  await dashboardDb.xperienceTractionMetrics.delete(`${userId}_${projectId}`);
}

// ---------- Extra-task cooldown (global per project, all stages share one timer) ----------
export async function getXpExtraTaskTs(userId, projectId) {
  const row = await dashboardDb.xperienceExtraTaskTs.get(`${userId}_${projectId}`);
  return row?.ts || null;
}
export async function saveXpExtraTaskTs(userId, projectId, ts) {
  const key = `${userId}_${projectId}`;
  await dashboardDb.xperienceExtraTaskTs.put({ key, userId, projectId, ts });
}
export async function deleteXpExtraTaskTs(userId, projectId) {
  await dashboardDb.xperienceExtraTaskTs.delete(`${userId}_${projectId}`);
}

// ---------- Used task IDs (per stage, avoids repeats until pool exhausted) ----------
export async function getXpUsedTasks(userId, projectId, stage) {
  const row = await dashboardDb.xperienceUsedTasks.get(`${userId}_${projectId}_${stage}`);
  return row?.data || [];
}
export async function saveXpUsedTasks(userId, projectId, stage, taskIds) {
  const key = `${userId}_${projectId}_${stage}`;
  await dashboardDb.xperienceUsedTasks.put({ key, userId, projectId, stage, data: taskIds });
}
export async function clearXpUsedTasks(userId, projectId, stage) {
  await dashboardDb.xperienceUsedTasks.delete(`${userId}_${projectId}_${stage}`);
}
export async function deleteAllXpUsedTasksForProject(userId, projectId) {
  await dashboardDb.xperienceUsedTasks.where({ userId, projectId }).delete();
}

// ---------- Seen learning-card stages ----------
export async function getXpSeenStage(userId, projectId, stage) {
  return !!(await dashboardDb.xperienceSeenStages.get(`${userId}_${projectId}_${stage}`));
}
export async function markXpSeenStage(userId, projectId, stage) {
  const key = `${userId}_${projectId}_${stage}`;
  await dashboardDb.xperienceSeenStages.put({ key, userId, projectId, stage });
}
export async function deleteAllXpSeenStagesForProject(userId, projectId) {
  await dashboardDb.xperienceSeenStages.where({ userId, projectId }).delete();
}

// ---------- Watched video resources (per user, global across all projects) ----------
export async function getXpWatchedResources(userId) {
  const row = await dashboardDb.xperienceWatchedResources.get(userId);
  return row?.data || {};
}
export async function saveXpWatchedResources(userId, data) {
  await dashboardDb.xperienceWatchedResources.put({ userId, data, updatedAt: Date.now() });
}

// ---------- Project creation quota ----------
export async function getXpProjectQuota(userId) {
  const row = await dashboardDb.xperienceProjectQuota.get(userId);
  return row?.data || { expansions: [], capHitAt: null };
}
export async function saveXpProjectQuota(userId, quota) {
  await dashboardDb.xperienceProjectQuota.put({ userId, data: quota, updatedAt: Date.now() });
}

// ---------- Stage info CMS cache (shared shape with LeaderDashboard's CMS caches) ----------
export async function getXpStageInfoCache(userId, stage) {
  return (await dashboardDb.xperienceStageInfoCache.get(`${userId}_${stage}`)) || null;
}
export async function saveXpStageInfoCache(userId, stage, data) {
  const key = `${userId}_${stage}`;
  await dashboardDb.xperienceStageInfoCache.put({ key, userId, stage, data, timestamp: Date.now() });
}
export async function bustXpStageInfoCache() {
  await dashboardDb.xperienceStageInfoCache.clear();
}

// ================== COACH THE COACH DASHBOARD ==================

// ---------- Completed resources (per skill, per user) ----------
export async function getCoachCompletedResources(userId, skillIdentifier) {
  const row = await dashboardDb.coachCompletedResources.get(`${userId}_${skillIdentifier}`);
  return row?.data || {};
}
export async function saveCoachCompletedResources(userId, skillIdentifier, data) {
  const key = `${userId}_${skillIdentifier}`;
  await dashboardDb.coachCompletedResources.put({ key, userId, skillIdentifier, data, updatedAt: Date.now() });
}

// ---------- CoachXcel notes (general + per-resource, one row per user) ----------
export async function getCoachXcelNotes(userId) {
  const row = await dashboardDb.coachXcelNotes.get(userId);
  return row?.data || {};
}
export async function saveCoachXcelNotes(userId, data) {
  await dashboardDb.coachXcelNotes.put({ userId, data, updatedAt: Date.now() });
}

// ---------- Coach facet descriptions cache (global, not user-scoped) ----------
export async function getCoachFacetDescriptionsCache() {
  return (await dashboardDb.coachFacetDescriptionsCache.get('global')) || null;
}
export async function saveCoachFacetDescriptionsCache(data) {
  await dashboardDb.coachFacetDescriptionsCache.put({ id: 'global', data, timestamp: Date.now() });
}
export async function bustCoachFacetDescriptionsCache() {
  await dashboardDb.coachFacetDescriptionsCache.clear();
}

// ---------- Coach skills cache (global, not user-scoped) ----------
export async function getCoachSkillsCache() {
  return (await dashboardDb.coachSkillsCache.get('global')) || null;
}
export async function saveCoachSkillsCache(data) {
  await dashboardDb.coachSkillsCache.put({ id: 'global', data, timestamp: Date.now() });
}
export async function bustCoachSkillsCache() {
  await dashboardDb.coachSkillsCache.clear();
}

// ---------- Coach learning resources cache (global, not user-scoped) ----------
export async function getCoachLearningResourcesCache() {
  return (await dashboardDb.coachLearningResourcesCache.get('global')) || null;
}
export async function saveCoachLearningResourcesCache(data) {
  await dashboardDb.coachLearningResourcesCache.put({ id: 'global', data, timestamp: Date.now() });
}
export async function bustCoachLearningResourcesCache() {
  await dashboardDb.coachLearningResourcesCache.clear();
}

// ---------- Coach feed categories cache (global, not user-scoped) ----------
export async function getCoachFeedCategoriesCache() {
  return (await dashboardDb.coachFeedCategoriesCache.get('global')) || null;
}
export async function saveCoachFeedCategoriesCache(data) {
  await dashboardDb.coachFeedCategoriesCache.put({ id: 'global', data, timestamp: Date.now() });
}
export async function bustCoachFeedCategoriesCache() {
  await dashboardDb.coachFeedCategoriesCache.clear();
}

// ---------- Coach assessment result cache (per user) ----------
export async function getCoachAssessmentCache(userId) {
  return (await dashboardDb.coachAssessmentCache.get(userId)) || null;
}
export async function saveCoachAssessmentCache(userId, data) {
  await dashboardDb.coachAssessmentCache.put({ userId, data, timestamp: Date.now() });
}

// ================== COACHING DAILY PRACTICE ==================

// ---------- Daily content cache (per user, date, difficulty) ----------
export async function getCoachDailyContentCache(userId, dateKey, difficulty) {
  const key = `${userId}_${dateKey}_${difficulty}`;
  const row = await dashboardDb.coachDailyContentCache.get(key);
  return row || null; // { key, userId, dateKey, difficulty, data, timestamp }
}

export async function saveCoachDailyContentCache(userId, dateKey, difficulty, data) {
  const key = `${userId}_${dateKey}_${difficulty}`;
  await dashboardDb.coachDailyContentCache.put({ key, userId, dateKey, difficulty, data, timestamp: Date.now() });
}

// Remove any cached daily content for this user that isn't for today.
// Safe to call on every mount — it's a no-op once already pruned.
export async function pruneCoachDailyContentCache(userId, currentDateKey) {
  await dashboardDb.coachDailyContentCache
    .where('userId').equals(userId)
    .and(row => row.dateKey !== currentDateKey)
    .delete();
}

// ---------- Full progress store (streak, reflections, completedDays, journal, per-slot answers) ----------
export async function getCoachDailyProgress(userId) {
  const row = await dashboardDb.coachDailyProgress.get(userId);
  return row?.data || null;
}

export async function saveCoachDailyProgress(userId, data) {
  await dashboardDb.coachDailyProgress.put({ userId, data, updatedAt: Date.now() });
}

// ---------- Seen competencies (avoids repeating the same competency across days) ----------
export async function getCoachDailySeenCompetencies(userId) {
  const row = await dashboardDb.coachDailySeenCompetencies.get(userId);
  return row?.data || [];
}

export async function saveCoachDailySeenCompetencies(userId, data) {
  await dashboardDb.coachDailySeenCompetencies.put({ userId, data, updatedAt: Date.now() });
}

// ---------- Selected difficulty ----------
export async function getCoachDailySelectedDifficulty(userId) {
  const row = await dashboardDb.coachDailySelectedDifficulty.get(userId);
  return row?.value || null;
}

export async function saveCoachDailySelectedDifficulty(userId, value) {
  await dashboardDb.coachDailySelectedDifficulty.put({ userId, value, updatedAt: Date.now() });
}

// ---------- Manual "New Practice" refresh cooldown timestamp ----------
export async function getCoachDailyRefreshCooldown(userId) {
  const row = await dashboardDb.coachDailyRefreshCooldown.get(userId);
  return row?.ts || null;
}

export async function saveCoachDailyRefreshCooldown(userId, ts) {
  await dashboardDb.coachDailyRefreshCooldown.put({ userId, ts });
}

// ================== LEADERSHIP DASHBOARD ==================

// ---------- Completed resources (per skill, per user) ----------
export async function getLeaderCompletedResources(userId, skillIdentifier) {
  const row = await dashboardDb.leaderCompletedResources.get(`${userId}_${skillIdentifier}`);
  return row?.data || {};
}
export async function saveLeaderCompletedResources(userId, skillIdentifier, data) {
  const key = `${userId}_${skillIdentifier}`;
  await dashboardDb.leaderCompletedResources.put({ key, userId, skillIdentifier, data, updatedAt: Date.now() });
}

// ---------- LeaderXcel notes (general + per-resource, one row per user) ----------
export async function getLeaderXcelNotes(userId) {
  const row = await dashboardDb.leaderXcelNotes.get(userId);
  return row?.data || {};
}
export async function saveLeaderXcelNotes(userId, data) {
  await dashboardDb.leaderXcelNotes.put({ userId, data, updatedAt: Date.now() });
}

// ---------- Leader facet descriptions cache (global, not user-scoped) ----------
export async function getLeaderFacetDescriptionsCache() {
  return (await dashboardDb.leaderFacetDescriptionsCache.get('global')) || null;
}
export async function saveLeaderFacetDescriptionsCache(data) {
  await dashboardDb.leaderFacetDescriptionsCache.put({ id: 'global', data, timestamp: Date.now() });
}
export async function bustLeaderFacetDescriptionsCache() {
  await dashboardDb.leaderFacetDescriptionsCache.clear();
}

// ---------- Leader skills cache (global, not user-scoped) ----------
export async function getLeaderSkillsCache() {
  return (await dashboardDb.leaderSkillsCache.get('global')) || null;
}
export async function saveLeaderSkillsCache(data) {
  await dashboardDb.leaderSkillsCache.put({ id: 'global', data, timestamp: Date.now() });
}
export async function bustLeaderSkillsCache() {
  await dashboardDb.leaderSkillsCache.clear();
}

// ---------- Leader learning resources cache (global, not user-scoped) ----------
export async function getLeaderLearningResourcesCache() {
  return (await dashboardDb.leaderLearningResourcesCache.get('global')) || null;
}
export async function saveLeaderLearningResourcesCache(data) {
  await dashboardDb.leaderLearningResourcesCache.put({ id: 'global', data, timestamp: Date.now() });
}
export async function bustLeaderLearningResourcesCache() {
  await dashboardDb.leaderLearningResourcesCache.clear();
}

// ---------- Leader feed categories cache (global, not user-scoped) ----------
export async function getLeaderFeedCategoriesCache() {
  return (await dashboardDb.leaderFeedCategoriesCache.get('global')) || null;
}
export async function saveLeaderFeedCategoriesCache(data) {
  await dashboardDb.leaderFeedCategoriesCache.put({ id: 'global', data, timestamp: Date.now() });
}
export async function bustLeaderFeedCategoriesCache() {
  await dashboardDb.leaderFeedCategoriesCache.clear();
}

// ---------- Leader assessment result cache (per user) ----------
export async function getLeaderAssessmentCache(userId) {
  return (await dashboardDb.leaderAssessmentCache.get(userId)) || null;
}
export async function saveLeaderAssessmentCache(userId, data) {
  await dashboardDb.leaderAssessmentCache.put({ userId, data, timestamp: Date.now() });
}

// ---------- STAR notes (per skill, shared/live across all jobs) ----------
const normalizeSkillName = (skillName) =>
  (skillName || '').trim().toLowerCase().replace(/\s+/g, ' ');

export async function getStarNoteBySkill(userId, skillName) {
  const key = `${userId}_${normalizeSkillName(skillName)}`;
  return (await dashboardDb.starNotesBySkill.get(key)) || null;
}

export async function saveStarNoteBySkill(userId, skillName, fields) {
  const key = `${userId}_${normalizeSkillName(skillName)}`;
  const existing = await dashboardDb.starNotesBySkill.get(key);
  const updated = {
    key, userId, skillName,
    s: existing?.s || '', t: existing?.t || '', a: existing?.a || '', r: existing?.r || '',
    ...fields,
    updatedAt: new Date().toISOString(),
  };
  await dashboardDb.starNotesBySkill.put(updated);
  return updated;
}

export async function getSkillProgressBySkill(userId, skillName) {
  const key = `${userId}_${normalizeSkillName(skillName)}`;
  const row = await dashboardDb.skillProgressBySkill.get(key);
  return row?.value ?? 0;
}

export async function saveSkillProgressBySkill(userId, skillName, value) {
  const key = `${userId}_${normalizeSkillName(skillName)}`;
  await dashboardDb.skillProgressBySkill.put({ key, userId, skillName, value, updatedAt: new Date().toISOString() });
}

// ---------- Bulk fetch: all skill progress rows for a user ----------
export async function getAllSkillProgressForUser(userId) {
  return await dashboardDb.skillProgressBySkill.where('userId').equals(userId).toArray();
}

export async function clearStarNoteBySkill(userId, skillName) {
  const key = `${userId}_${normalizeSkillName(skillName)}`;
  await dashboardDb.starNotesBySkill.delete(key);
}

export async function getAllStarNotesBySkillForUser(userId) {
  return await dashboardDb.starNotesBySkill.where('userId').equals(userId).toArray();
}

// Records that a given job also touches this skill (kept for the STAR Bank digest's job-title lookup)
export async function addStarIndexEntry(userId, jobpostId, skillName) {
  const index = await getStarIndex(userId);
  const exists = index.some((e) => String(e.jobpostId) === String(jobpostId) && e.skillName === skillName);
  if (!exists) {
    await saveStarIndex(userId, [...index, { jobpostId: String(jobpostId), skillName }]);
  }
}

// then add, alongside getPortfolioJobs / savePortfolioJobs:
export async function getJobTrackerEntries(userId) {
  const row = await dashboardDb.jobTrackerEntries.get(userId);
  return row?.data || [];
}

export async function saveJobTrackerEntries(userId, entries) {
  await dashboardDb.jobTrackerEntries.put({ userId, data: entries });
}

// ---------- Learner Intelligence (career direction, concerns, values, goals) ----------
export async function getLearnerIntelligence(userId) {
  const row = await dashboardDb.learnerIntelligence.get(userId);
  return row?.data || null;
}

export async function saveLearnerIntelligence(userId, data) {
  await dashboardDb.learnerIntelligence.put({ userId, data, updatedAt: Date.now() });
}

// ---------- Study Learner Intelligence (StudyStyle/Strategy reflection) ----------
export async function getStudyLearnerIntelligence(userId) {
  const row = await dashboardDb.studyLearnerIntelligence.get(userId);
  return row?.data || null;
}

export async function saveStudyLearnerIntelligence(userId, data) {
  await dashboardDb.studyLearnerIntelligence.put({ userId, data, updatedAt: Date.now() });
}

// ---------- InterviewPro: student-added custom interview questions (per job) ----------
export async function getInterviewCustomQuestions(userId, jobpostId) {
  const key = `${userId}_${jobpostId}`;
  const row = await dashboardDb.interviewCustomQuestions.get(key);
  return row?.data || [];
}

export async function saveInterviewCustomQuestions(userId, jobpostId, data) {
  const key = `${userId}_${jobpostId}`;
  await dashboardDb.interviewCustomQuestions.put({ key, userId, jobpostId: String(jobpostId), data, updatedAt: Date.now() });
}

// Bulk-load ALL custom questions for a user into a map: { [jobpostId]: [...] }
// Mirrors the shape CareerDashboard.js previously kept in localStorage as one JSON blob.
export async function getAllInterviewCustomQuestionsForUser(userId) {
  const rows = await dashboardDb.interviewCustomQuestions.where('userId').equals(userId).toArray();
  const map = {};
  rows.forEach(r => { map[r.jobpostId] = r.data || []; });
  return map;
}

// ================== LEADERSHIP DAILY PRACTICE ==================

// ---------- Daily content cache (per user, date, difficulty) ----------
export async function getLeaderDailyContentCache(userId, dateKey, difficulty) {
  const key = `${userId}_${dateKey}_${difficulty}`;
  const row = await dashboardDb.leaderDailyContentCache.get(key);
  return row || null; // { key, userId, dateKey, difficulty, data, timestamp }
}

export async function saveLeaderDailyContentCache(userId, dateKey, difficulty, data) {
  const key = `${userId}_${dateKey}_${difficulty}`;
  await dashboardDb.leaderDailyContentCache.put({ key, userId, dateKey, difficulty, data, timestamp: Date.now() });
}

// Remove any cached daily content for this user that isn't for today.
// Safe to call on every mount — it's a no-op once already pruned.
export async function pruneLeaderDailyContentCache(userId, currentDateKey) {
  await dashboardDb.leaderDailyContentCache
    .where('userId').equals(userId)
    .and(row => row.dateKey !== currentDateKey)
    .delete();
}

// ---------- Full progress store (streak, reflections, completedDays, journal, per-slot answers) ----------
export async function getLeaderDailyProgress(userId) {
  const row = await dashboardDb.leaderDailyProgress.get(userId);
  return row?.data || null;
}

export async function saveLeaderDailyProgress(userId, data) {
  await dashboardDb.leaderDailyProgress.put({ userId, data, updatedAt: Date.now() });
}

// ---------- Seen competencies (avoids repeating the same competency across days) ----------
export async function getLeaderDailySeenCompetencies(userId) {
  const row = await dashboardDb.leaderDailySeenCompetencies.get(userId);
  return row?.data || [];
}

export async function saveLeaderDailySeenCompetencies(userId, data) {
  await dashboardDb.leaderDailySeenCompetencies.put({ userId, data, updatedAt: Date.now() });
}

// ---------- Selected difficulty ----------
export async function getLeaderDailySelectedDifficulty(userId) {
  const row = await dashboardDb.leaderDailySelectedDifficulty.get(userId);
  return row?.value || null;
}

export async function saveLeaderDailySelectedDifficulty(userId, value) {
  await dashboardDb.leaderDailySelectedDifficulty.put({ userId, value, updatedAt: Date.now() });
}

// ---------- Manual "New Practice" refresh cooldown timestamp ----------
export async function getLeaderDailyRefreshCooldown(userId) {
  const row = await dashboardDb.leaderDailyRefreshCooldown.get(userId);
  return row?.ts || null;
}

export async function saveLeaderDailyRefreshCooldown(userId, ts) {
  await dashboardDb.leaderDailyRefreshCooldown.put({ userId, ts });
}

const FACET_NOTE_CACHE_VERSION = 'v4'; // bumped: v3 busted visualizationsCache, which
                                        // isn't what feeds the Career/Learning radar —
                                        // the facet notes (incl. low/mid/high) are built
                                        // client-side from cached `rawAnswers` rows.

export async function ensureFacetNoteCacheFreshness(userId) {
  if (!userId) return;
  const flagKey = `facet_note_cache_${FACET_NOTE_CACHE_VERSION}_${userId}`;
  if (localStorage.getItem(flagKey) === 'done') return;

  const careerRawKey = `${userId}_Career`;
  const learningRawKey = `${userId}_Learning`;

  try {
    // This is the table that actually feeds createRadarChartData/buildFacetNoteData —
    // clear it so the next load re-fetches fresh answers (with low/mid/high notes)
    // from the server instead of serving a pre-schema-change cached row.
    await dashboardDb.rawAnswers.bulkDelete([careerRawKey, learningRawKey]);

    await Promise.allSettled([
      dashboardDb.rawAnswers.where('userId').equals(userId).delete(),
      dashboardDb.rawAnswers.where('userId').equals(String(userId)).delete(),
      // Keep clearing this too — harmless, and covers anything that might
      // still read it (e.g. a future re-introduction, or other pages).
      dashboardDb.visualizationsCache.where('userId').equals(userId).delete(),
      dashboardDb.visualizationsCache.where('userId').equals(String(userId)).delete(),
    ]);

    // Legacy localStorage snapshots used as PDF-generation fallbacks
    localStorage.removeItem(`visualizations_${userId}_Career`);
    localStorage.removeItem(`visualizations_${userId}_Learning`);

    const [stillCareer, stillLearning] = await Promise.all([
      dashboardDb.rawAnswers.get(careerRawKey),
      dashboardDb.rawAnswers.get(learningRawKey),
    ]);
    if (stillCareer || stillLearning) {
      throw new Error('rawAnswers rows survived the bust attempt');
    }

    console.log(`[facet-note-bust] Cleared stale Career/Learning raw-answer cache for user ${userId}`);

    localStorage.setItem(flagKey, 'done');
  } catch (err) {
    console.warn('[facet-note-bust] Could not clear facet-note cache — will retry next load:', err);
  }
}

// ---------- Subject facets cache (global, not user-scoped) — full facet
// list from /api/questions/facets, incl. facet_note. Populated once and
// reused both by "Choose Your Top Subject" and the PDF export, instead of
// each caller re-fetching or hitting the per-facet endpoint. ----------
export async function getSubjectFacetsCache() {
  return (await dashboardDb.subjectFacetsCache.get('global')) || null; // { id, data, timestamp }
}

export async function saveSubjectFacetsCache(data) {
  await dashboardDb.subjectFacetsCache.put({ id: 'global', data, timestamp: Date.now() });
}

export async function bustSubjectFacetsCache() {
  await dashboardDb.subjectFacetsCache.clear();
}